import React, { useEffect, useState, useCallback } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSidebar } from '../../contexts/SidebarContext';
import api from '../../utils/api';
import { showSuccess, showError } from '../../utils/toast';
import {
  Droplet,
  Plus,
  Search,
  Trash2,
  Edit2,
  CheckCircle2,
  XCircle,
  Phone,
  MapPin,
  User,
  ShieldCheck,
  RefreshCw,
  X
} from 'lucide-react';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const FATEHABAD_AREAS = [
  'Fatehabad', 'Ratia', 'Bhattu Kalan', 'Jakhal', 'Tohana',
  'Bhuna', 'Gorakhpur', 'Kulan', 'Tigrana', 'Pili Mandori',
  'Ludesar', 'Dhansu', 'Nehla', 'Bhirdana', 'Dhani Phogat', 'Other'
];

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
  inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-700',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
};

const BG_BADGE = {
  'A+': 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border-red-200 dark:border-red-800',
  'A-': 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200 dark:border-rose-800',
  'B+': 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  'B-': 'bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  'AB+': 'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  'AB-': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
  'O+': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  'O-': 'bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border-teal-200 dark:border-teal-800',
};

const EMPTY_FORM = {
  name: '',
  bloodGroup: '',
  phone: '',
  area: 'Fatehabad',
  image: '',
  isAvailable: true,
  status: 'active'
};

const AdminBloodDonorsContent = () => {
  const { isCollapsed } = useSidebar();
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filterGroup, setFilterGroup] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Add / Edit Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const fetchDonors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (filterGroup) params.set('bloodGroup', filterGroup);
      if (filterStatus) params.set('status', filterStatus);
      const res = await api.get(`/admin/blood-donors?${params}`);
      setDonors(res.data.donors || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) {
      console.error('Error fetching donors:', err);
      setDonors([]);
    } finally {
      setLoading(false);
    }
  }, [page, filterGroup, filterStatus]);

  useEffect(() => {
    fetchDonors();
  }, [fetchDonors]);

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (donor) => {
    setEditingId(donor._id);
    setFormData({
      name: donor.name || '',
      bloodGroup: donor.bloodGroup || '',
      phone: donor.phone || '',
      area: donor.area || 'Fatehabad',
      image: donor.image || '',
      isAvailable: donor.isAvailable !== false,
      status: donor.status || 'active'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showError('Please enter donor full name');
      return;
    }
    if (!formData.bloodGroup) {
      showError('Please select a blood group');
      return;
    }
    if (!formData.phone.trim() || formData.phone.trim().length < 10) {
      showError('Please enter a valid 10-digit phone number');
      return;
    }
    if (!formData.area) {
      showError('Please select an area');
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/admin/blood-donors/${editingId}`, formData);
        showSuccess('Donor updated successfully!');
      } else {
        await api.post('/admin/blood-donors', formData);
        showSuccess('Blood donor added successfully!');
      }
      setShowModal(false);
      fetchDonors();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save donor.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.patch(`/admin/blood-donors/${id}/status`, { status });
      showSuccess(`Status changed to ${status}`);
      setDonors(prev => prev.map(d => d._id === id ? { ...d, status } : d));
    } catch (err) {
      showError('Failed to update status');
    }
  };

  const handleToggleAvailable = async (id, current) => {
    try {
      await api.patch(`/admin/blood-donors/${id}/status`, { isAvailable: !current });
      showSuccess(`Availability marked as ${!current ? 'Available' : 'Unavailable'}`);
      setDonors(prev => prev.map(d => d._id === id ? { ...d, isAvailable: !current } : d));
    } catch (err) {
      showError('Failed to toggle availability');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to remove ${name || 'this donor'}?`)) return;
    try {
      await api.delete(`/admin/blood-donors/${id}`);
      showSuccess('Donor removed successfully');
      setDonors(prev => prev.filter(d => d._id !== id));
      setTotal(prev => Math.max(0, prev - 1));
    } catch (err) {
      showError('Failed to remove donor');
    }
  };

  const filteredDonors = donors.filter(d => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      d.name?.toLowerCase().includes(q) ||
      d.phone?.toLowerCase().includes(q) ||
      d.area?.toLowerCase().includes(q) ||
      d.bloodGroup?.toLowerCase().includes(q)
    );
  });

  return (
    <>
      <SEO title="Admin - Blood Donors Management" description="Manage Fatehabad blood donors directory" noindex />

      <main className={`flex-1 transition-all duration-300 min-h-screen ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container-custom py-6 md:py-10 max-w-7xl mx-auto px-4">
          
          {/* Header & Add Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 flex items-center justify-center shadow-sm">
                  <Droplet className="w-6 h-6 fill-current" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    Blood Donors Directory
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Manage emergency blood donors in Fatehabad district ({total} total)
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={fetchDonors}
                className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
                title="Refresh Donors"
              >
                <RefreshCw className="w-5 h-5" />
              </button>

              {/* PRIMARY ACTION: ADD DONOR */}
              <button
                id="admin-add-blood-donor-btn"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-semibold rounded-xl transition-all shadow-md hover:shadow-lg active:scale-95 text-sm"
              >
                <Plus className="w-5 h-5" />
                Add Blood Donor
              </button>
            </div>
          </div>

          {/* Quick Filters Card */}
          <div className="card p-4 md:p-5 rounded-2xl mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              {/* Search */}
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search name, phone, area..."
                  className="w-full pl-10 pr-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Blood Group Filter */}
              <div>
                <select
                  value={filterGroup}
                  onChange={e => { setFilterGroup(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">All Blood Groups</option>
                  {BLOOD_GROUPS.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <select
                  value={filterStatus}
                  onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="inactive">Inactive</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="card rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
            {loading ? (
              <div className="py-20 text-center">
                <PageLoader message="Loading blood donors directory..." />
              </div>
            ) : filteredDonors.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 dark:bg-red-950/40 text-red-500 flex items-center justify-center">
                  <Droplet className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">No Blood Donors Found</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                  {searchQuery || filterGroup || filterStatus
                    ? 'Try adjusting your filters or search keywords.'
                    : 'Start by adding verified blood donors to help people in Fatehabad district.'}
                </p>
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm transition-all shadow-md"
                >
                  <Plus className="w-4 h-4" /> Add First Blood Donor
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-800 text-xs uppercase font-semibold text-gray-600 dark:text-gray-400">
                    <tr>
                      <th className="px-5 py-3.5">Donor Name</th>
                      <th className="px-5 py-3.5">Group</th>
                      <th className="px-5 py-3.5">Contact Phone</th>
                      <th className="px-5 py-3.5">Area / Block</th>
                      <th className="px-5 py-3.5">Source</th>
                      <th className="px-5 py-3.5">Status</th>
                      <th className="px-5 py-3.5 text-center">Available</th>
                      <th className="px-5 py-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {filteredDonors.map((donor) => (
                      <tr
                        key={donor._id}
                        className="hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors"
                      >
                        {/* Name */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            {donor.image ? (
                              <img
                                src={donor.image}
                                alt={donor.name}
                                className="w-8 h-8 rounded-full object-cover border border-red-300 flex-shrink-0"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 font-bold text-xs flex items-center justify-center flex-shrink-0 border border-red-200">
                                {donor.name?.charAt(0) || '🩸'}
                              </div>
                            )}
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white">
                                {donor.name}
                              </div>
                              {donor.userId && (
                                <span className="text-[11px] text-gray-400">
                                  Linked: {donor.userId.name || donor.userId.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Blood Group */}
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center justify-center font-black text-xs px-2.5 py-1 rounded-lg border ${BG_BADGE[donor.bloodGroup] || 'bg-gray-100 text-gray-800'}`}>
                            {donor.bloodGroup}
                          </span>
                        </td>

                        {/* Phone */}
                        <td className="px-5 py-4 font-mono text-gray-700 dark:text-gray-300 text-xs">
                          <a
                            href={`tel:${donor.phone}`}
                            className="inline-flex items-center gap-1.5 hover:text-red-600 transition-colors"
                          >
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            {donor.phone}
                          </a>
                        </td>

                        {/* Area */}
                        <td className="px-5 py-4 text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            {donor.area}
                          </span>
                        </td>

                        {/* Source (Admin vs User) */}
                        <td className="px-5 py-4">
                          {donor.addedByAdmin ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                              <ShieldCheck className="w-3 h-3" /> Admin
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                              <User className="w-3 h-3" /> User
                            </span>
                          )}
                        </td>

                        {/* Status Select */}
                        <td className="px-5 py-4">
                          <select
                            value={donor.status}
                            onChange={(e) => handleStatusChange(donor._id, e.target.value)}
                            className={`text-xs font-semibold px-2.5 py-1 rounded-lg border cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-400 ${STATUS_COLORS[donor.status]}`}
                          >
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="inactive">Inactive</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </td>

                        {/* Available Toggle */}
                        <td className="px-5 py-4 text-center">
                          <button
                            onClick={() => handleToggleAvailable(donor._id, donor.isAvailable)}
                            title={donor.isAvailable ? 'Click to mark Unavailable' : 'Click to mark Available'}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              donor.isAvailable ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-700'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                donor.isAvailable ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEditModal(donor)}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Edit Donor"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(donor._id, donor.name)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Delete Donor"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="p-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-sm">
                <span className="text-gray-500 text-xs">Page {page} of {pages}</span>
                <div className="flex gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-semibold disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Previous
                  </button>
                  <button
                    disabled={page === pages}
                    onClick={() => setPage(p => Math.min(pages, p + 1))}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-semibold disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </main>

      {/* ── MODAL: ADD / EDIT BLOOD DONOR ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-800 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-rose-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5 text-white">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <Droplet className="w-5 h-5 fill-current" />
                </div>
                <div>
                  <h2 className="font-bold text-lg leading-tight">
                    {editingId ? 'Edit Blood Donor' : 'Add Blood Donor'}
                  </h2>
                  <p className="text-xs text-white/80">Fatehabad District Blood Bank Registry</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body / Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              
              {/* Photo Input (URL or upload) */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Donor Photo (URL or File)
                </label>
                <div className="flex items-center gap-3">
                  {formData.image ? (
                    <img
                      src={formData.image}
                      alt="Donor"
                      className="w-12 h-12 rounded-xl object-cover border border-red-300 shadow-sm flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-400 text-lg flex-shrink-0">
                      📷
                    </div>
                  )}
                  <input
                    type="text"
                    value={formData.image}
                    onChange={e => setFormData({ ...formData, image: e.target.value })}
                    placeholder="Paste image URL..."
                    className="flex-1 px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  {formData.image && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image: '' })}
                      className="text-xs text-red-500 hover:text-red-700 px-2 py-1"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Ramesh Kumar"
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Blood Group Chips */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                  Blood Group <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {BLOOD_GROUPS.map(group => {
                    const isSelected = formData.bloodGroup === group;
                    return (
                      <button
                        type="button"
                        key={group}
                        onClick={() => setFormData({ ...formData, bloodGroup: group })}
                        className={`py-2 px-3 rounded-xl border-2 font-black text-sm transition-all ${
                          isSelected
                            ? 'bg-red-600 border-red-600 text-white shadow-md scale-102'
                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-red-300'
                        }`}
                      >
                        {group}
                      </button>
                    );
                  })}
                </div>
                {!formData.bloodGroup && (
                  <p className="text-xs text-red-500 mt-1">Please pick a blood group</p>
                )}
              </div>

              {/* Phone & Area */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9+]/g, '') })}
                    placeholder="10 digit mobile"
                    maxLength={14}
                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Area / Block <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.area}
                    onChange={e => setFormData({ ...formData, area: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {FATEHABAD_AREAS.map(area => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Status Select & Availability */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Donor Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="active">Active (Verified)</option>
                    <option value="pending">Pending Review</option>
                    <option value="inactive">Inactive</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div className="flex flex-col justify-end">
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                    Availability
                  </label>
                  <div className="flex items-center gap-3 py-1">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, isAvailable: !formData.isAvailable })}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        formData.isAvailable ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-700'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          formData.isAvailable ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {formData.isAvailable ? '✅ Ready to donate' : '⛔ On cooldown / paused'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 text-sm font-semibold rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !formData.bloodGroup}
                  className="flex-1 py-2.5 text-sm font-bold rounded-xl bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white transition-all shadow-md disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingId ? 'Update Donor' : 'Save Donor'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </>
  );
};

const AdminBloodDonors = () => (
  <AdminLayout>
    <AdminBloodDonorsContent />
  </AdminLayout>
);

export default AdminBloodDonors;
